<template>
  <v-dialog
      v-model="showingDialog"
      v-bind:disabled="noUsers"
      persistent
      max-height="680"
      scrollable
      :fullscreen="$vuetify.display.xs"
      :width="$vuetify.display.smAndUp ? 600 : 'auto'"
      style="z-index: 1100 !important;"
    >

    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="userManagement-options-deleteUsers-activator">
        <slot></slot>
      </div>
    </template>

    <div class="vb-dialog vb-dialog-danger">
      <v-card v-cloak data-cy="dialog-deleteSelectedUsers-modal">
        <v-card-title class="d-flex justify-space-between mb-2">
          {{$t('routes.users.deleteUsers')}}

          <v-btn icon @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="scrollable">
          <span v-if="noUsers" style="font-size: 16px;">
              {{$t('routes.users.usersNotSelected')}}
          </span>
          <span v-else-if="selfUserSelected" style="font-size: 16px;">
              {{$t('routes.users.cannotDisableYourself')}}
          </span>
          <span v-else style="font-size:16px;">
              {{$t('routes.users.confirmDeleteUsers', users.length)}}
          </span>

          <div
            v-if="!noUsers && !selfUserSelected && associatedResourcesWarning"
            class="mt-2 tw-warning"
          >
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.users.deleteAssociatedResourcesWarning') }}</span>
          </div>

          <div
            v-else-if="!noUsers && !selfUserSelected && (associatedFtpUsers > 0 || associatedApiClients > 0)"
            class="mt-2 tw-warning"
          >
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.users.deleteAssociatedResourcesInfo') }}</span>
            <ul class="mx-5 mt-1">
              <li v-if="associatedFtpUsers > 0">
                {{ $t('routes.users.deleteAssociatedFtpUsers', associatedFtpUsers) }}
              </li>
              <li v-if="associatedApiClients > 0">
                {{ $t('routes.users.deleteAssociatedApiClients', associatedApiClients) }}
              </li>
            </ul>
          </div>

          <v-row class="mt-4">
            <v-col>
              <div v-if="errorMessage" class="bg-error mb-4" style="width: 100%;">
                {{ errorMessage }}
              </div>

              <div v-if="successMessage" class="bg-success mb-4" style="width: 100%;">
                {{ successMessage }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions v-if="noUsers || selfUserSelected">
          <v-btn class="ml-auto" @click="closeDialog()" data-cy="dialog-deleteSelectedUsers-noUsersOrSelf-false">{{$t('common.close')}}</v-btn>
        </v-card-actions>

        <v-card-actions v-else>
          <v-btn class="ml-auto bg-red-darken-3" elevation=0 @click="deleteUsers" data-cy="dialog-deleteSelectedUsers-ok">
            {{$t('common.delete')}}
          </v-btn>
          <v-btn variant="text" @click="closeDialog()" class="btnCancel" data-cy="dialog-deleteSelectedUsers-false">
            {{$t('common.cancel')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import UserCalls from '@/api/modules/UserCalls.js';

export default {
  emits: ["usersDeleted", 'update:modelValue'],
  mixins: [DialogActionKeys],
  name: "DeleteUsersDialog",

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    users: {
      type: Array,
      required: true,
    }
  },

  data: function() {
    return {
      errorMessage: '',
      successMessage: '',
      associatedFtpUsers: 0,
      associatedApiClients: 0,
      associatedResourcesWarning: false,
    };
  },

  watch: {
    modelValue(newValue) {
      if (newValue) {
        this.resetForm();
        this.fetchAssociatedResources();
      }
    }
  },
  methods: {
    resetForm() {
      this.errorMessage = '';
      this.successMessage = '';
      this.associatedFtpUsers = 0;
      this.associatedApiClients = 0;
      this.associatedResourcesWarning = false;
    },
    async fetchAssociatedResources() {
      if (this.noUsers || this.selfUserSelected) {
        return;
      }

      const authStore = useAuthStore();
      const rootNodeId = authStore.rootNodeId;

      try {
        const resourceCounts = await Promise.all(this.users.map(async (user) => {
          const response = await UserCalls.getAssociatedResources({
            accessPointId: rootNodeId,
            userId: user.id,
          });

          return {
            ftpUsers: response?.data?.ftpUsers ?? 0,
            apiClients: response?.data?.apiClients ?? 0,
          };
        }));

        this.associatedFtpUsers = resourceCounts.reduce((total, counts) => total + counts.ftpUsers, 0);
        this.associatedApiClients = resourceCounts.reduce((total, counts) => total + counts.apiClients, 0);
      } catch {
        this.associatedResourcesWarning = true;
      }
    },
    closeDialog() {
      this.showingDialog = false;
    },
    deleteUsers: async function() {
      const promises = [];

      const authStore = useAuthStore();
      const accessPointId = authStore.rootNodeId;

      for (const user of this.users) {
        const payload = {
          userId: user.id,
          accessPointId,
        }

        const userStore = useUserStore();
        const promise = userStore.detachUser(payload);
        promises.push(promise);
      }

      if (promises.length > 0) {
        try {
          await Promise.all(promises);

          this.$emit('usersDeleted');
          this.closeDialog();
        } catch (error) {
          console.log('error', error);
          let errorMessage = this.$t('routes.users.errorDeletingUsers');

          if (error && error.response && error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error.message;
            this.errorMessage = errorMessage;
          }

          this.$showMessage(errorMessage, { type: 'error' });
        }
      }
    },
  },

  computed: {
    noUsers: function() {
      return !this.users || (Array.isArray(this.users) && this.users.length === 0);
    },

    dbUser: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    selfUserSelected: function() {
      if (this.noUsers) {
        return false;
      }

      const filteredUsers = this.users.filter((user) => user.id === this.dbUser.id);
      return filteredUsers.length > 0;
    },
    showingDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    }
  }
};
</script>

<style>

</style>
